/* The chart column: the matrix, the legend that isolates an action, the bar under it, and
   the standing note at the bottom. */

import {
  type Spot,
  actionLabel,
  equityNote,
  equityRows,
  LABEL,
  ORDER,
  seatName,
} from "@preflop/core";
import { el, fill } from "../../ui/dom";
import { ACTION_COLOR } from "../../ui/grid";
import { canPaint } from "./brush";
import { setNote } from "./flash";
import { dom, grid } from "./layout";
import { chartSpot, showDraftState } from "./preview";
import { currentRoll } from "./randomizer";
import { page, render } from "./state";

function renderMatrix(spot: Spot): void {
  const p = page();
  grid.render({
    spot,
    isolate: p.isolate,
    // Reading, the randomizer answers the chart: every hand is painted the one action this
    // roll names. Editing, the mix is what the brush works on, and isolating an action is
    // already a filter — a second one on top of it only hides which cells the first one kept.
    roll: !p.editMode && !p.isolate ? currentRoll() : null,
    // Equity against every opponent's range, once per chart rather than per hover: 169 x 169
    // multiply-adds per opponent, well under a millisecond.
    equity: equityRows(p.equity, p.index, spot),
    paint: canPaint(),
    label: [seatName(spot.hero), actionLabel(spot.action), ...spot.seats.map(seatName)].join(" · "),
    id: spot.key,
  });
}

function renderLegend(spot: Spot): void {
  const p = page();
  const blocks: HTMLElement[] = [];
  const segments: HTMLElement[] = [];
  for (const a of ORDER) {
    const st = spot.stats[a];
    if (!st) continue;
    const b = el(
      "button",
      {
        type: "button",
        class:
          "block" + (p.isolate && p.isolate !== a ? " dim" : "") + (p.isolate === a ? " sel" : ""),
        title: p.isolate === a ? "Show every action again" : `Show ${LABEL[a]} on its own`,
        onclick: () => {
          p.isolate = p.isolate === a ? null : a;
          render();
        },
      },
      el("b", {}, LABEL[a]),
      el("i", {}, el("em", {}, `${st.pct}%`), el("small", {}, `${st.combos} combos`)),
    );
    b.style.setProperty("--c", ACTION_COLOR[a]);
    blocks.push(b);
    segments.push(el("div", { style: { width: `${st.pct}%`, background: ACTION_COLOR[a] } }));
  }
  fill(dom.legend, blocks);
  fill(dom.bar, segments);
}

/** The chart column on its own. The panel being typed into is not rebuilt, so the caret
 *  stays where it is. */
export function paintChart(spot?: Spot | null): void {
  const p = page();
  const sp = spot ?? p.spot;
  if (!sp) return;
  const shown = chartSpot(sp);
  renderMatrix(shown);
  renderLegend(shown);
  setNote(
    [
      shown.exact === false
        ? `${shown.totalCombos} combos in play. Hero's range is inferred from the parent chart, ` +
          `so per-action frequencies are exact but the total may be off by a few combos.`
        : "",
      // While the table is still on its way there is nothing to report either way.
      p.equityLoaded ? equityNote(p.equity, p.index, shown) : "",
    ]
      .filter(Boolean)
      .join(" "),
  );
  showDraftState();
}

/* A burst of keystrokes repaints once. 169 cells are cheap, the parse is not. */
let previewTimer: number | null = null;
export function cancelPreview(): void {
  if (previewTimer !== null) clearTimeout(previewTimer);
  previewTimer = null;
}
export function previewNow(): void {
  cancelPreview();
  previewTimer = window.setTimeout(() => {
    previewTimer = null;
    paintChart();
  }, 120);
}
